/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Search from '@components/Hotels/Search';
import { useRouter } from 'next/router';
import { srpPageViewed } from '@events/SRP';
import { useCookies } from 'react-cookie';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { isArray } from 'lodash';
import useURLDate from '@hooks/useURLDate';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import { getExperimentValue } from '@utils/amplitudeSSR';
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import useSendAmplitudeExposureV2 from '@hooks/useSendAmplitudeExposureV2';
import EducationalModuleModal from '@components/EducationalModule/EducationalModuleModal';
import { ExperimentType } from '@customTypes/context';
import { ReferralCampaignFeatureFlagType } from '@context/FeatureFlagContext';
import { GetCanonicalURL } from '@helpers/environment';
import { getLocations } from '@utils/services';
import { getUserAndDeviceID } from '@helpers/amplitude';
import { useEvents } from '@events/EventsProvider';
import { EXCLUDE_FROM_SEARCH_FEATURE_FLAG } from '@constants/amplitudeEvents';
import { GenerateSearchUrl, MergeSearchParams } from '@helpers/url';
import LOCATION_TYPE from '@constants/locationTypes';
import { GetDefaultSearchState } from '@context/SearchContext2025';

type LocationParam = string | string[] | undefined;

/**
 * Helper function to handle location-based redirects
 * @param locationName - Name of the location (country, state, or hotel)
 * @param locationType - Type of location (COUNTRY, STATE, or HOTEL)
 * @returns Redirect URL or null if no redirect needed
 */
async function handleLocationRedirect(
  locationName: LocationParam,
  locationType: string,
): Promise<string | null> {
  if (!locationName) return null;
  const name = Array.isArray(locationName) ? locationName[0] : locationName;
  const locations = await getLocations(`"${name}"`);
  const locationData = locations.find(
    (loc) =>
      loc.type === locationType && loc.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );

  const cityData = {
    name: locationData?.city_name || '',
    state_code: locationData?.state_code || '',
  };

  // Create a SearchState object
  const searchState = GetDefaultSearchState();

  // For state redirects, ensure the state_code is set correctly
  if (locationType === LOCATION_TYPE.STATE) {
    // If we have location data, use its state_code
    if (locationData && locationData.state_code) {
      searchState.state_code = locationData.state_code;
    } else {
      // If no location data, use the name as the state_code (lowercase)
      searchState.state_code = name.toLowerCase();
    }
  }

  // For hotel redirects, set the city and state_code
  if (locationType === LOCATION_TYPE.HOTEL && cityData) {
    searchState.city = cityData.name;
    searchState.state_code = cityData.state_code;
    searchState.hotel_name = name;
  }

  searchState.country_code = locationData?.country_code || 'us';
  return GenerateSearchUrl(searchState);
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { query } = context;
  const { state, country_name, hotel_name } = query;
  const contextUrl = context.req.url || '';

  // Handle country redirect
  let redirectUrl = await handleLocationRedirect(country_name, LOCATION_TYPE.COUNTRY);

  // If no country redirect, try state redirect
  if (!redirectUrl) {
    redirectUrl = await handleLocationRedirect(state, LOCATION_TYPE.STATE);
  }

  // If no state redirect, try hotel redirect
  if (!redirectUrl && hotel_name) {
    redirectUrl = await handleLocationRedirect(hotel_name, LOCATION_TYPE.HOTEL);
  }

  // If we have a redirect URL, merge it with the context URL and return a permanent redirect
  if (redirectUrl) {
    // Merge the redirect URL with the context URL to preserve any query parameters
    const mergedRedirectUrl = MergeSearchParams(redirectUrl, contextUrl);
    return {
      redirect: {
        destination: mergedRedirectUrl,
        permanent: true,
      },
    };
  }

  const educationalModuleExperiment = await getExperimentValue<
    typeof AMPLITUDE_EXPERIMENT_VARIANT_NAME | typeof AMPLITUDE_EXPERIMENT_CONTROL_NAME | undefined
  >(context, 'educational-module-banner-experiment-v2');

  const pricingMarkdownExperiment = await getExperimentValue<
    typeof AMPLITUDE_EXPERIMENT_VARIANT_NAME | typeof AMPLITUDE_EXPERIMENT_CONTROL_NAME | undefined
  >(context, 'stp-weekday-rate');

  const [referralCampaign] = await Promise.all([
    getExperimentValue<ReferralCampaignFeatureFlagType>(context, 'referral-campaign').catch(
      () => null,
    ),
  ]);

  // Get the device id or the user id.
  const { deviceId, userId } = getUserAndDeviceID(context);
  const amplitudeUserId = userId || deviceId;

  return {
    props: {
      educationalModuleExperiment,
      pricingMarkdownExperiment,
      referralCampaign,
      canonicalURL: context.req.url ? GetCanonicalURL(context.req.url) : '',
      amplitudeUserId,
    },
  };
}

type Props = {
  educationalModuleExperiment: ExperimentType;
  pricingMarkdownExperiment: ExperimentType;
  canonicalURL: string;
  amplitudeUserId: string;
};

export default function SearchPage({
  canonicalURL,
  educationalModuleExperiment,
  pricingMarkdownExperiment,
  amplitudeUserId,
}: Props) {
  const [titleHotelName, setTitleHotelName] = useState<string>('');
  const [isSearchWithoutQuery, setIsSearchWithoutQuery] = useState<boolean>(false);
  const [date, setDate] = useState<string | undefined>();
  const [srpViewEventSent, setSrpViewEventSent] = useState<boolean>(false);

  const router = useRouter();
  const { dispatch } = useContext(store);

  const { state, country_name, hotel_id } = router.query;
  const { getItem } = useSessionStorage();
  const [cookies] = useCookies();
  const urlDate = useURLDate();
  const { track } = useEvents();

  const storedDate = getItem(sessionStorageKeys.SMART_CALENDAR_DATE);
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);
  const selectedLocation = getItem(sessionStorageKeys.SEARCHED_LOCATION);

  useSendAmplitudeExposureV2({
    flagKey: 'stp-weekday-rate',
    validation: !!pricingMarkdownExperiment,
    variant: pricingMarkdownExperiment,
  });

  useSendAmplitudeExposureV2({
    flagKey: 'educational-module-banner-experiment-v2',
    validation: !!educationalModuleExperiment,
    variant: educationalModuleExperiment,
  });

  useEffect(() => {
    track(EXCLUDE_FROM_SEARCH_FEATURE_FLAG, {
      page: window.location.href,
      amplitude_user_id: amplitudeUserId,
      referrer: document.referrer,
    });
  }, [amplitudeUserId, track]);

  useEffect(() => {
    if (pricingMarkdownExperiment) {
      dispatch({
        type: actionTypes.SET_PRICING_MARKDOWN_EXPERIMENT,
        payload: pricingMarkdownExperiment,
      });
    }
  }, [dispatch, pricingMarkdownExperiment]);

  useEffect(() => {
    if (educationalModuleExperiment) {
      dispatch({
        type: actionTypes.SET_EDUCATIONAL_MODULE_EXPERIMENT,
        payload: educationalModuleExperiment,
      });
    }
  }, [dispatch, educationalModuleExperiment]);

  useEffect(() => {
    setDate(urlDate || storedDate);
  }, [urlDate, storedDate]);

  useEffect(() => {
    if (window && window.location) {
      if (
        window.location.href.indexOf('state=') === -1 &&
        window.location.href.indexOf('country_name=') === -1 &&
        window.location.href.indexOf('city_id=') === -1
      ) {
        setIsSearchWithoutQuery(true);
      } else {
        setIsSearchWithoutQuery(false);
      }
    }
  }, [router]);

  useEffect(() => {
    if (!srpViewEventSent) {
      const location = selectedLocation ? JSON.parse(selectedLocation) : null;
      const country = isArray(country_name) ? country_name[0] : country_name;
      const stateCode = isArray(state) ? state[0] : state;

      // Event
      srpPageViewed(
        date,
        cookies.userInformation,
        sessionID,
        location,
        country,
        stateCode,
        undefined,
      );

      setSrpViewEventSent(true);
    }
  }, [
    cookies.userInformation,
    country_name,
    date,
    selectedLocation,
    sessionID,
    srpViewEventSent,
    state,
  ]);

  return (
    <>
      <Head>
        <title>Best Pool & Spa Passes in {titleHotelName} | Hotel Day Passes | ResortPass</title>
        <meta
          name="description"
          content={`Enjoy the pool, spa, and amenities for the day at a luxury ${titleHotelName} hotel. Take a daycation and book a day pass or cabana at hotels and resorts near you!`}
        />
        {canonicalURL ? <link rel="canonical" href={canonicalURL} /> : null}
      </Head>

      <Search
        name={
          state
            ? `hotel-day-passes?state=${state}`
            : country_name
            ? `hotel-day-passes?country_name=${country_name}`
            : hotel_id
            ? `hotel-day-passes?hotel_name=${hotel_id}&highlighted=true`
            : ''
        }
        searchWithoutQuery={isSearchWithoutQuery}
        setTitleHotelName={setTitleHotelName}
      />

      {educationalModuleExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
        <EducationalModuleModal />
      ) : null}
    </>
  );
}
